import clsx from 'clsx'
import {USER_TYPES} from 'common/constants'
import {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {RootState} from 'setup'
import {getStudentHouseLabel} from 'setup/redux/Actions/User/user'

/** The house badge the API serves (`GET user/house-label`). */
export type StudentHouse = {
  name: string
  /** `#RRGGBB`, or null when the label has no colour */
  color: string | null
  /** the readable text colour on `color`, or null with it */
  textColor: string | null
}

const HEX = /^#[0-9A-F]{6}$/i

/**
 * Reads the API's `house` defensively: anything that is not a house with a
 * name is "no badge", and a colour that is not `#RRGGBB` is dropped so the
 * badge falls back to the theme's neutral one instead of an invalid style.
 */
export const parseStudentHouse = (value: unknown): StudentHouse | null => {
  if (!value || typeof value !== 'object') return null
  const {name, color, textColor} = value as Record<string, unknown>
  if (typeof name !== 'string' || !name.trim()) return null
  const validColor = typeof color === 'string' && HEX.test(color) ? color : null
  return {
    name: name.trim(),
    color: validColor,
    textColor:
      validColor && typeof textColor === 'string' && HEX.test(textColor) ? textColor : null,
  }
}

/**
 * The signed-in student's house, in its label colour. From `lg` up it is the
 * first thing in the header, beside the sidebar toggle (HeaderWrapper); on
 * narrower screens it is the first of the right-hand items (Navbar).
 *
 * Renders nothing unless the user is a student, the school's General Settings
 * switch `show_student_house_label` is on, and the API returns a house — so a
 * school that has not run the migration (no such setting), a student in no
 * house, or a failed request all simply show no badge.
 */
const StudentHouseBadge: FC<{className?: string}> = ({className}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [house, setHouse] = useState<StudentHouse | null>(null)

  const isStudentUser = useSelector<RootState>(
    ({userReducer}) => userReducer.userType === USER_TYPES.STUDENT
  ) as boolean
  const userId = useSelector<RootState>(({userReducer}) => userReducer.user?.id) as
    number | undefined
  const enabled = useSelector<RootState>(
    ({mainReducer}) => Number(mainReducer.generalSettings?.show_student_house_label ?? 0) === 1,
    shallowEqual
  ) as boolean

  useEffect(() => {
    if (!enabled || !isStudentUser) {
      setHouse(null)
      return
    }
    let cancelled = false
    dispatch(
      getStudentHouseLabel(
        (response: any) => {
          if (!cancelled) setHouse(parseStudentHouse(response?.result?.house))
        },
        () => {
          if (!cancelled) setHouse(null)
        }
      )
    )
    return () => {
      cancelled = true
    }
  }, [dispatch, enabled, isStudentUser, userId])

  if (!house) return null

  const label = intl.formatMessage({id: 'PROFILE.HOUSE_BADGE'}, {name: house.name})

  return (
    <div className={clsx('app-navbar-item', className)}>
      <span
        data-testid='student-house-badge'
        role='status'
        aria-label={label}
        title={label}
        className={clsx(
          'badge fw-bold fs-7 px-3 py-2 d-inline-block text-truncate mw-100px mw-sm-150px mw-lg-200px',
          !house.color && 'badge-light text-gray-800'
        )}
        style={
          house.color
            ? {
                backgroundColor: house.color,
                color: house.textColor ?? undefined,
                // a white or near-background house colour still reads as a badge
                boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.12)',
              }
            : undefined
        }
      >
        {house.name}
      </span>
    </div>
  )
}

export default StudentHouseBadge
